import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { warehouseAPI, customerAPI } from '../services/api';
import { getCartonStatusBadge, formatDateTime } from '../utils/helpers';
import toast from 'react-hot-toast';

function ReceiveCartonModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ customerId: '', shippingMark: '', supplierTrackingNumber: '', productDescription: '', quantity: 1, weightKg: '', cbm: '', notes: '' });
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    customerAPI.getAll().then(r => setCustomers(r.data.data || []));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await warehouseAPI.receive({ ...form, customerId: Number(form.customerId), quantity: Number(form.quantity), weightKg: Number(form.weightKg), cbm: Number(form.cbm) });
      toast.success('Carton received successfully!');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 580 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Receive Carton</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Customer *</label>
              <select className="form-select" value={form.customerId} onChange={e => setForm({ ...form, customerId: e.target.value })} required>
                <option value="">Select customer...</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name} ({c.customerCode})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Shipping Mark</label>
              <input className="form-input" value={form.shippingMark} onChange={e => setForm({ ...form, shippingMark: e.target.value })} placeholder="RAHIM-DHK-001" />
            </div>
            <div className="form-group">
              <label className="form-label">Supplier Tracking No.</label>
              <input className="form-input" value={form.supplierTrackingNumber} onChange={e => setForm({ ...form, supplierTrackingNumber: e.target.value })} placeholder="SF1234567890CN" />
            </div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Product Description</label>
              <input className="form-input" value={form.productDescription} onChange={e => setForm({ ...form, productDescription: e.target.value })} placeholder="Clothing items - 50 pieces" />
            </div>
            <div className="form-group">
              <label className="form-label">Weight (KG)</label>
              <input className="form-input" type="number" step="0.01" value={form.weightKg} onChange={e => setForm({ ...form, weightKg: e.target.value })} placeholder="15.50" />
            </div>
            <div className="form-group">
              <label className="form-label">CBM</label>
              <input className="form-input" type="number" step="0.0001" value={form.cbm} onChange={e => setForm({ ...form, cbm: e.target.value })} placeholder="0.085" />
            </div>
            <div className="form-group">
              <label className="form-label">Quantity</label>
              <input className="form-input" type="number" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} min="1" />
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <input className="form-input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Handle with care..." />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" style={{ width: 14, height: 14 }} /> : null}
              Receive Carton
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ScanModal({ onClose, onSaved }) {
  const [barcode, setBarcode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleScan = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await warehouseAPI.bdScan(barcode.trim());
      setResult(res.data.data);
      toast.success('Barcode scanned — carton received in BD!');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Barcode not found');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">🇧🇩 Scan Barcode (BD Receive)</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>
        <form onSubmit={handleScan}>
          <div className="form-group">
            <label className="form-label">Barcode Value</label>
            <input className="form-input" value={barcode} onChange={e => setBarcode(e.target.value)} placeholder="CGO-1-1234567890" autoFocus required />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Scan barcode from carton sticker or type manually</span>
          </div>
          <button type="submit" className="btn btn-success" disabled={loading} style={{ width: '100%', justifyContent: 'center' }}>
            {loading ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '⟳'}
            {loading ? 'Scanning...' : 'Scan & Confirm Receipt'}
          </button>
        </form>
        {result && (
          <div style={{ marginTop: 20, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 8, padding: 16 }}>
            <div style={{ color: 'var(--success)', fontWeight: 600, marginBottom: 8 }}>✓ Carton Confirmed</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>GRN: <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>{result.grnNumber}</span></div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>Status: <span className={`badge ${getCartonStatusBadge(result.status)}`}>{result.status}</span></div>
            {result.totalProductTypes > 0 && (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                Products: <strong>{result.totalProductTypes} type(s)</strong>, <strong>{result.totalItems} pcs</strong> total
                <span style={{ marginLeft: 10, fontSize: 12, color: 'var(--accent)' }}>→ Go to BD Receiving for full details & photos</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Warehouse() {
  const [cartons, setCartons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    warehouseAPI.getAll()
      .then(res => setCartons(res.data.data || []))
      .catch(() => toast.error('Failed to load cartons'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleGenerateBarcode = async (id) => {
    try {
      await warehouseAPI.generateBarcode(id);
      toast.success('Barcode generated!');
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleMarkDelivered = async (id) => {
    if (!window.confirm('Mark this carton as delivered to customer?')) return;
    try {
      await warehouseAPI.markDelivered(id);
      toast.success('Carton marked as delivered!');
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const filtered = cartons.filter(c => {
    const matchSearch = !search || c.grnNumber?.includes(search) || c.shippingMark?.toLowerCase().includes(search.toLowerCase()) || c.barcode?.includes(search);
    const matchStatus = !statusFilter || c.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const statuses = ['RECEIVED','SORTING','REGISTERED','GROUPED','PACKED','DEPARTED','IN_TRANSIT','ARRIVED_BD','IN_BD_WAREHOUSE','DELIVERED'];

  return (
    <div className="animate-fade">
      <div className="page-header">
        <h1 className="page-title">Warehouse</h1>
        <p className="page-subtitle">{cartons.length} total cartons</p>
      </div>

      <div className="action-bar">
        <div style={{ display: 'flex', gap: 10, flex: 1, flexWrap: 'wrap' }}>
          <input className="form-input" style={{ maxWidth: 240 }} placeholder="Search GRN, mark, barcode..." value={search} onChange={e => setSearch(e.target.value)} />
          <select className="form-select" style={{ maxWidth: 180 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            {statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={() => navigate('/bd-receive')} style={{ color: '#10b981', borderColor: '#10b98144' }}>🇧🇩 BD Receive</button>
          <button className="btn btn-secondary" onClick={() => setModal('scan')}>⟳ Scan Barcode</button>
          <button className="btn btn-primary" onClick={() => setModal('receive')}>+ Receive Carton</button>
        </div>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>GRN Number</th>
              <th>Customer ID</th>
              <th>Shipping Mark</th>
              <th>Description</th>
              <th>Weight</th>
              <th>CBM</th>
              <th>Barcode</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40 }}><span className="spinner" /></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={10}><div className="empty-state"><div className="empty-state-icon">▣</div><div className="empty-state-text">No cartons found</div></div></td></tr>
            ) : filtered.map(c => (
              <tr key={c.id}>
                <td><span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent)' }}>{c.grnNumber}</span></td>
                <td><span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{c.customerId}</span></td>
                <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{c.shippingMark || '—'}</td>
                <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.productDescription || '—'}</td>
                <td>{c.weightKg ? `${c.weightKg} kg` : '—'}</td>
                <td>{c.cbm || '—'}</td>
                <td>
                  {c.barcode
                    ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--success)' }}>{c.barcode}</span>
                    : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Not generated</span>
                  }
                </td>
                <td><span className={`badge ${getCartonStatusBadge(c.status)}`}>{c.status}</span></td>
                <td style={{ fontSize: 12 }}>{formatDateTime(c.createdAt)}</td>
                <td>
                  {!c.barcode && (
                    <button className="btn btn-secondary btn-sm" onClick={() => handleGenerateBarcode(c.id)}>Gen Barcode</button>
                  )}
                  {c.status === 'IN_BD_WAREHOUSE' && (
                    <button className="btn btn-success btn-sm" onClick={() => handleMarkDelivered(c.id)}>Deliver</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal === 'receive' && <ReceiveCartonModal onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal === 'scan' && <ScanModal onClose={() => setModal(null)} onSaved={() => { load(); }} />}
    </div>
  );
}
